import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  HStack,
  Stack,
  Text,
  Textarea,
  useDisclosure,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { useTranslations } from 'use-intl';

import {
  FundraisingDocumentType,
  SubscriptionInfosFragment,
  useGetDocumentPdfUrlLazyQuery,
  useGetSubscriptionQuery,
  useRefuseFundsSourceCertificateMutation,
} from '@blockpulse3/graphql/hooks';
import { CONTENT_LOADING, DeleteConfirmModal, PreviewDocumentModal } from '@blockpulse3/ui/commons';

type Props = {
  subscriptionId: SubscriptionInfosFragment['id'];
};

export function ValidateFundsSourceCertificate({ subscriptionId }: Props): JSX.Element | null {
  const i18nSubscriptionDetails = useTranslations(
    'SubscriberDetailsSidePanel.ValidationSteps.SubscriptionDetails',
  );
  const i18nDocumentTitle = useTranslations('DocumentValues');

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const deleteRef = useRef(null);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const deleteModal = useDisclosure();
  const [documentUrl, setDocumentUrl] = useState<string | symbol | null>(CONTENT_LOADING);

  /* ** Cached subscription query ** */
  const { data, loading, refetch } = useGetSubscriptionQuery({ variables: { subscriptionId } });

  /* ** Query to get pdf url of a document ** */
  const [getDocumentPdfUrl] = useGetDocumentPdfUrlLazyQuery();

  const [refuseCertificate] = useRefuseFundsSourceCertificateMutation();

  const subscription = data?.subscription;
  const subscriptionDocuments = subscription?.subscriptionDocuments || [];
  const fundsSourceCertificate = subscriptionDocuments.find(
    (doc) => doc.type === FundraisingDocumentType.FUNDS_SOURCE_CERTIFICATE,
  );

  if (!fundsSourceCertificate) return null;

  const handleOpenClick = (): void => {
    getDocumentPdfUrl({
      variables: {
        documentId: fundsSourceCertificate.id,
      },
      fetchPolicy: 'no-cache',
      onCompleted: ({ getDocumentPdfUrl: pdfUrl }) => {
        setDocumentUrl(pdfUrl);
        onOpen();
      },
    });
  };

  const handleCertificateRefused = (): void => {
    const customMessage = inputRef.current?.value || undefined;

    refuseCertificate({
      variables: {
        refuseFundsSourceCertificateInput: {
          subscriptionId,
          customMessage,
        },
      },
      onCompleted: () => {
        refetch();
        onClose();
      },
    });
  };

  return (
    <>
      <Button
        isDisabled={loading}
        isLoading={loading}
        size="sm"
        variant="secondary"
        onClick={handleOpenClick}
      >
        {i18nSubscriptionDetails('Consult')}
      </Button>
      <PreviewDocumentModal
        isOpen={isOpen}
        src={documentUrl}
        title={i18nDocumentTitle('DocumentFUNDS_SOURCE_CERTIFICATE')}
        onClose={onClose}
      >
        <Stack gap="4" w="full">
          <Accordion allowToggle variant="unstyled">
            <AccordionItem>
              <AccordionButton data-cy="expand-parameters">
                <Text color="gray" fontSize="sm" textAlign="left">
                  {i18nSubscriptionDetails('SelectLabel')}
                </Text>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel p="0">
                <Textarea minH="200" ref={inputRef} size="sm" />
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
          <HStack alignSelf="end">
            <Button variant="secondary" onClick={deleteModal.onOpen}>
              {i18nSubscriptionDetails('Refuse')}
            </Button>
            <Button onClick={onClose}>{i18nSubscriptionDetails('Validate')}</Button>
          </HStack>
        </Stack>
      </PreviewDocumentModal>
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        leastDestructiveRef={deleteRef}
        subtitle={i18nSubscriptionDetails('DeleteModal.SubTitle')}
        title={i18nSubscriptionDetails('DeleteModal.Title')}
        onClose={deleteModal.onClose}
        onDelete={handleCertificateRefused}
      />
    </>
  );
}
